import {
  Elements,
  PaymentElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import { Stripe, loadStripe } from "@stripe/stripe-js";
import { useEffect, useState } from "react";

import { IObjectWithTypename } from "@/components/@types";
import { useRestApi } from "@/hooks/useRestApi";
import { Spinner } from "@/components/ui/spinner";
import { Button } from "@/components/ui/button";
import { Typography } from "@/components/ui/typography";

interface PaymentButtonProps {
  node: IObjectWithTypename;
  amount: number;
  currency: string;
  /**
   * The instalment being paid, when the invoice is paid on a schedule. The
   * server charges that instalment's amount and settles it on the webhook, so
   * this is what separates "pay the deposit" from "pay the invoice".
   */
  paymentScheduleItemIdentifier?: string | null;
  onSuccess?: (paymentIntentId: string) => void;
  onError?: (error: string) => void;
}

interface PaymentFormProps {
  clientSecret: string;
  publishableKey: string;
  amount: number;
  currency: string;
  onSuccess: (paymentIntentId: string) => void;
  onError: (error: string) => void;
  onClose: () => void;
  node: IObjectWithTypename;
}

function StripePaymentIntentForm({
  clientSecret,
  publishableKey,
  amount,
  currency,
  onSuccess,
  onError,
  onClose,
  node,
}: PaymentFormProps) {
  const stripe = useStripe();
  const elements = useElements();
  const [isProcessing, setIsProcessing] = useState(false);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setIsProcessing(true);

    try {
      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: `${window.location.origin}/public/${window.location.pathname.split("/").pop()}?payment=success`,
        },
        redirect: "if_required",
      });

      if (error) {
        onError(error.message || "Payment failed");
      } else if (paymentIntent && paymentIntent.status === "succeeded") {
        onSuccess(paymentIntent.id);
        onClose();
      }
    } catch (error) {
      onError(
        error instanceof Error ? error.message : "Payment processing failed",
      );
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <PaymentElement />
      <div className="flex gap-2 mt-6 justify-end">
        <Button variant="outline" onClick={onClose} disabled={isProcessing}>
          Cancel
        </Button>
        <Button
          type="submit"
          disabled={isProcessing || !stripe || !elements || isProcessing}
        >
          {isProcessing && <Spinner />}
          {isProcessing
            ? "Processing..."
            : `Pay $${amount.toFixed(2)} ${currency.toUpperCase()}`}
        </Button>
      </div>
    </form>
  );
}

export function StripePaymentIntentFlow({
  node,
  amount,
  currency,
  paymentScheduleItemIdentifier,
  onSuccess,
  onError,
}: PaymentButtonProps) {
  const [isCreating, setIsCreating] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [paymentData, setPaymentData] = useState<{
    clientSecret: string;
    publishableKey: string;
  } | null>(null);
  const [stripePromise, setStripePromise] =
    useState<Promise<Stripe | null> | null>(null);
  const { post } = useRestApi();

  // Create payment intent on mount
  useEffect(() => {
    let isMounted = true;

    const createPaymentIntent = async () => {
      try {
        await post(
          "/v1/integrations/stripe/payment-intent",
          {
            document_identifier: node.identifier,
            payment_schedule_item_identifier:
              paymentScheduleItemIdentifier ?? null,
            // Back to the page paid on, by the id it is read by.
            success_url: `${window.location.origin}/public/${node.id}?payment=success`,
            cancel_url: `${window.location.origin}/public/${node.id}?payment=cancelled`,
          },
          {
            callbacks: {
              onSuccess: (data) => {
                if (!isMounted) return;
                if (
                  data.success &&
                  data.client_secret &&
                  data.publishable_key
                ) {
                  const promise = loadStripe(data.publishable_key);
                  setStripePromise(promise);
                  setPaymentData({
                    clientSecret: data.client_secret,
                    publishableKey: data.publishable_key,
                  });
                } else {
                  setError("Failed to initialize payment");
                  onError?.("Failed to initialize payment");
                }
                setIsCreating(false);
              },
              onError: (err) => {
                if (!isMounted) return;
                setError(err);
                onError?.(err);
                setIsCreating(false);
              },
            },
          },
        );
      } catch (err) {
        if (!isMounted) return;
        const message =
          err instanceof Error ? err.message : "Failed to initialize payment";
        setError(message);
        onError?.(message);
        setIsCreating(false);
      }
    };

    createPaymentIntent();

    return () => {
      isMounted = false;
    };
  }, [node.identifier, paymentScheduleItemIdentifier]);

  const handlePaymentSuccess = (paymentIntentId: string) => {
    onSuccess?.(paymentIntentId);
    setPaymentData(null);
    setStripePromise(null);
  };

  const handlePaymentError = (err: string) => {
    onError?.(err);
  };

  const handleClose = () => {
    setPaymentData(null);
    setStripePromise(null);
  };

  return (
    <div className="flex flex-col gap-4 w-full p-4">
      {isCreating && (
        <div className="flex items-center justify-center gap-3 pt-6 pb-6">
          <Spinner />
          <Typography level="body-sm" className="text-muted-foreground">
            Loading payment form...
          </Typography>
        </div>
      )}

      {error && !isCreating && (
        <Typography level="body-sm" className="text-destructive">
          {error}
        </Typography>
      )}

      {paymentData && stripePromise && !isCreating && (
        <Elements
          stripe={stripePromise}
          options={{ clientSecret: paymentData.clientSecret }}
        >
          <StripePaymentIntentForm
            node={node}
            clientSecret={paymentData.clientSecret}
            publishableKey={paymentData.publishableKey}
            amount={amount}
            currency={currency}
            onSuccess={handlePaymentSuccess}
            onError={handlePaymentError}
            onClose={handleClose}
          />
        </Elements>
      )}
    </div>
  );
}
